import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} SellingPlanPickerProperties
 * @property {HTMLInputElement|null} quantityInput - The quantity input in the product form
 * @property {HTMLInputElement|null} sellingPlanInput - The hidden selling_plan input in the product form
 * @property {NodeListOf<HTMLInputElement>} bulkDiscountOptions - Bulk discount radio inputs
 * @property {NodeListOf<HTMLInputElement>} subscriptionRadios - Subscription/one-time radio inputs
 * @property {HTMLInputElement|null} oneTimePurchaseRadio - The one-time purchase radio input
 * @property {NodeListOf<HTMLInputElement>} deliveryFrequencyRadios - Delivery frequency radio inputs
 * @property {HTMLElement|null} bulkDiscountWrapper - Wrapper for bulk discount content (animated)
 * @property {HTMLElement|null} deliveryFrequencyWrapper - Wrapper for delivery frequency content (animated)
 */

class SellingPlanPicker extends HTMLElement {
  /** @type {AbortController|null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Cache elements — scope to nearest section/dialog to avoid grabbing the wrong form
    const container = this.closest('.shopify-section, dialog');
    const form = container?.querySelector('form[action="/cart/add"]:has(input[name="quantity"])');

    if (!form) {
      console.warn('[selling-plan-picker] No product form found. Requires form[action="/cart/add"] with quantity input inside a .shopify-section or dialog ancestor.');
      this.hidden = true;
      return;
    }

    this.quantityInput = form.querySelector('input[name="quantity"]');
    this.bulkDiscountOptions = this.querySelectorAll('[data-bulk-discount-option]');
    this.subscriptionRadios = this.querySelectorAll('input[name^="selling-plan-group-"]');
    this.oneTimePurchaseRadio = this.querySelector('.subscription-option--one-time input[type="radio"]');
    this.sellingPlanInput = this.#setupSellingPlanInput(form);
    this.deliveryFrequencyRadios = this.querySelectorAll('input[name^="delivery-frequency-"]');
    this.oneTimeSavingsBadge = this.querySelector('[data-one-time-savings-badge]');
    this.subscriptionSavingsBadge = this.querySelector('[data-subscription-savings-badge]');

    // Content wrappers for height animation
    this.bulkDiscountWrapper = this.querySelector('.selling-plan-picker__content-wrapper--bulk-discount');
    this.deliveryFrequencyWrapper = this.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency');

    // Radiogroup container that hosts the sliding pill's width vars.
    this.deliveryFrequencyGroup = this.querySelector('.delivery-frequency-options');
    /** @type {number[]} Stack of last two checked indices [current, previous]. */
    this.deliveryFrequencyCheckedIndices = [];
    this.#initDeliveryFrequencyPillState();

    this.#setupBulkDiscountQuantitySync(signal);
    this.#setupDeliveryFrequencyHandlers(signal);
    this.#setupVariantChangeHandler(signal);
    this.#setupCommitmentTooltips(signal);

    // Set initial state
    this.#syncState();
    this.#updateWrapperVisibility();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Finds or creates the hidden selling_plan input in the product form.
   * @param {HTMLFormElement|null} form
   * @returns {HTMLInputElement|null}
   */
  #setupSellingPlanInput(form) {
    if (!form) return null;

    // First check for our own input (with data attribute)
    let sellingPlanInput = form.querySelector('input[name="selling_plan"][data-selling-plan-input]');
    if (sellingPlanInput) return sellingPlanInput;

    // Then check for any existing selling_plan input (e.g., from an app or theme extension)
    sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) {
      sellingPlanInput.setAttribute('data-selling-plan-input', '');
      return sellingPlanInput;
    }

    // No input found, create one
    sellingPlanInput = document.createElement('input');
    sellingPlanInput.type = 'hidden';
    sellingPlanInput.name = 'selling_plan';
    sellingPlanInput.setAttribute('data-selling-plan-input', '');
    form.appendChild(sellingPlanInput);

    return sellingPlanInput;
  }

  /**
   * Updates the hidden selling_plan input value.
   * @param {string|null} sellingPlanId
   */
  #updateSellingPlan(sellingPlanId) {
    if (!this.sellingPlanInput) {
      if (sellingPlanId) {
        console.error('[selling-plan-picker] Cannot set selling plan: hidden input missing from form.');
      }
      return;
    }
    this.sellingPlanInput.value = sellingPlanId || '';
  }

  /**
   * Returns the minimum valid quantity from the quantity input's constraints.
   * Falls back to '1' if no input, no min attribute, or min is falsy (empty string, "0").
   * @returns {string}
   */
  get #defaultQuantity() {
    return this.quantityInput?.min || '1';
  }

  /**
   * Updates the quantity input and dispatches change events.
   * @param {string} quantity
   */
  #updateQuantity(quantity) {
    if (!this.quantityInput) {
      if (quantity && quantity !== '1') {
        console.error('[selling-plan-picker] Cannot set quantity: quantity input missing from form.');
      }
      return;
    }
    this.quantityInput.value = quantity;
    this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Returns the quantity from the currently checked bulk discount option, or the default quantity.
   * @returns {string}
   */
  get #oneTimeQuantity() {
    const checkedBulkOption = this.querySelector('[data-bulk-discount-option]:checked');
    return checkedBulkOption?.dataset.quantity || this.#defaultQuantity;
  }

  /**
   * Animates a content wrapper to the specified visibility state.
   * @param {HTMLElement|null} wrapper
   * @param {boolean} visible
   * @param {'wrapper'|'delivery-frequency'} infix - CSS variable infix (e.g., 'wrapper' produces --wrapper-height)
   */
  #animateWrapper(wrapper, visible, infix) {
    if (!wrapper) return;

    if (visible) {
      // Temporarily make visible to measure
      wrapper.style.setProperty(`--${infix}-height`, 'auto');
      wrapper.style.setProperty(`--${infix}-opacity`, '1');
      const height = wrapper.scrollHeight;
      // Set to 0 then animate to measured height
      wrapper.style.setProperty(`--${infix}-height`, '0px');
      wrapper.style.setProperty(`--${infix}-opacity`, '0');
      // Force reflow
      wrapper.offsetHeight;
      wrapper.style.setProperty(`--${infix}-height`, `${height}px`);
      wrapper.style.setProperty(`--${infix}-opacity`, '1');
      wrapper.style.setProperty('--sp-wrapper-translate', '0px');

      // Stagger bulk discount tiles on reveal
      if (infix === 'wrapper' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const tiles = wrapper.querySelectorAll('.bulk-discount-option');
        for (const [i, tile] of [...tiles].entries()) {
          tile.animate(
            [
              { opacity: 0, transform: 'translateY(4px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            { duration: 220, delay: i * 40, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'backwards' },
          );
        }
      }
    } else {
      wrapper.style.setProperty(`--${infix}-height`, '0px');
      wrapper.style.setProperty(`--${infix}-opacity`, '0');
      wrapper.style.setProperty('--sp-wrapper-translate', '4px');
    }
  }

  /** @returns {boolean} Whether the one-time purchase option is currently selected. */
  get #isOneTimePurchase() {
    return this.oneTimePurchaseRadio?.checked ?? true;
  }

  /**
   * Shows or hides the one-time purchase savings badge based on the selected
   * bulk discount tier's discount percentage.
   */
  #updateOneTimeSavingsBadge() {
    if (!this.oneTimeSavingsBadge) return;

    // Hide the badge while subscription is active to avoid confusion
    if (!this.#isOneTimePurchase) {
      this.oneTimeSavingsBadge.hidden = true;
      return;
    }

    const checked = this.querySelector('[data-bulk-discount-option]:checked');
    const percent = parseInt(checked?.dataset.discountPercent, 10) || 0;
    const wasHidden = this.oneTimeSavingsBadge.hidden;
    const oldText = this.oneTimeSavingsBadge.textContent;

    if (percent > 0) {
      const newText = this.oneTimeSavingsBadge.dataset.template.replace('PERCENT', percent);
      this.oneTimeSavingsBadge.textContent = newText;
      this.oneTimeSavingsBadge.hidden = false;

      // Animate value change when switching between tiers (not on first reveal — CSS handles that)
      if (!wasHidden && oldText !== newText && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.oneTimeSavingsBadge.getAnimations().forEach((a) => a.cancel());
        this.oneTimeSavingsBadge.animate(
          [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-3px)', offset: 0.3 },
            { opacity: 0, transform: 'translateY(3px)', offset: 0.31 },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 240, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        );
      }
    } else {
      this.oneTimeSavingsBadge.hidden = true;
    }
  }

  /**
   * Updates the "Subscribe & Save" badge to match the active delivery frequency's
   * savings percent. Falls back to the first available frequency input.
   */
  #updateSubscriptionSavingsBadge() {
    if (!this.subscriptionSavingsBadge) return;

    const source = this.querySelector('input[name^="delivery-frequency-"]:checked')
      || this.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency > input[type="hidden"][data-savings-percent]')
      || this.querySelector('input[name^="delivery-frequency-"]');
    const percent = parseInt(source?.dataset.savingsPercent, 10) || 0;
    const oldText = this.subscriptionSavingsBadge.textContent;

    if (percent > 0) {
      const template = this.subscriptionSavingsBadge.dataset.template || '';
      const newText = template.replace('PERCENT', percent);
      this.subscriptionSavingsBadge.textContent = newText;
      this.subscriptionSavingsBadge.hidden = false;

      if (oldText.trim() && oldText !== newText && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.subscriptionSavingsBadge.getAnimations().forEach((a) => a.cancel());
        this.subscriptionSavingsBadge.animate(
          [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-3px)', offset: 0.3 },
            { opacity: 0, transform: 'translateY(3px)', offset: 0.31 },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 240, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        );
      }
    } else {
      this.subscriptionSavingsBadge.hidden = true;
    }
  }

  /** Updates wrapper visibility based on current radio state. */
  #updateWrapperVisibility() {
    const isOneTime = this.#isOneTimePurchase;
    this.#animateWrapper(this.bulkDiscountWrapper, isOneTime, 'wrapper');
    this.#animateWrapper(this.deliveryFrequencyWrapper, !isOneTime, 'delivery-frequency');
  }

  /**
   * Syncs form state (selling plan + quantity) based on current radio selections.
   * Called on init and after subscription/one-time radio changes.
   */
  #syncState() {
    this.#updateOneTimeSavingsBadge();
    this.#updateSubscriptionSavingsBadge();
    if (this.#isOneTimePurchase) {
      this.#updateSellingPlan(null);
      this.#updateQuantity(this.#oneTimeQuantity);
    } else {
      // Subscription selected — prefer delivery frequency radio, fall back to subscription radio
      const checkedFrequencyRadio = this.querySelector('input[name^="delivery-frequency-"]:checked');
      const checkedSubscriptionRadio = this.querySelector('input[name^="selling-plan-group-"]:checked:not([value=""])');
      const sellingPlanId = checkedFrequencyRadio?.dataset.sellingPlanId
        || checkedSubscriptionRadio?.dataset.sellingPlanId
        || null;
      this.#updateSellingPlan(sellingPlanId);
      this.#updateQuantity(this.#defaultQuantity);
    }
    this.#dispatchPriceChange();
  }

  /**
   * Dispatches a custom event with current pricing details for external components
   * (e.g., the add-to-cart-with-price button) to react to.
   */
  #dispatchPriceChange() {
    const productId = this.dataset.productId;
    const variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;

    let unitPrice = variantPrice;
    let quantity = 1;
    let totalPrice = variantPrice;
    const isSubscription = !this.#isOneTimePurchase;

    if (isSubscription) {
      // Subscription: use adjusted price from delivery frequency radio or hidden input
      const checkedFrequencyRadio = this.querySelector('input[name^="delivery-frequency-"]:checked');
      const hiddenFrequencyInput = this.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency > input[type="hidden"][data-adjusted-price]');
      const source = checkedFrequencyRadio || hiddenFrequencyInput;
      if (source?.dataset.adjustedPrice) {
        unitPrice = parseInt(source.dataset.adjustedPrice, 10) || variantPrice;
      }
      quantity = 1;
      totalPrice = unitPrice;
    } else {
      // One-time purchase: use bulk discount tier if selected
      const checkedBulkOption = this.querySelector('[data-bulk-discount-option]:checked');
      if (checkedBulkOption) {
        quantity = parseInt(checkedBulkOption.dataset.quantity, 10) || 1;
        unitPrice = parseInt(checkedBulkOption.dataset.discountedPrice, 10) || variantPrice;
      }
      totalPrice = unitPrice * quantity;
    }

    this.dispatchEvent(new CustomEvent('selling-plan-picker:price-change', {
      bubbles: true,
      detail: { unitPrice, quantity, totalPrice, isSubscription, productId },
    }));
  }

  /**
   * Sets up bulk discount and subscription radio change handlers.
   * @param {AbortSignal} signal
   */
  #setupBulkDiscountQuantitySync(signal) {
    const hasSubscriptionOptions = this.subscriptionRadios.length > 0;

    for (const option of this.bulkDiscountOptions) {
      option.addEventListener('change', () => {
        if (!option.checked) return;
        if (!hasSubscriptionOptions || this.#isOneTimePurchase) {
          this.#updateQuantity(option.dataset.quantity || this.#defaultQuantity);
        }
        this.#updateOneTimeSavingsBadge();
        this.#dispatchPriceChange();
      }, { signal });
    }

    if (hasSubscriptionOptions) {
      for (const radio of this.subscriptionRadios) {
        radio.addEventListener('change', () => {
          this.#syncState();
          this.#updateWrapperVisibility();
        }, { signal });
      }
    }
  }

  /**
   * Sets up delivery frequency radio change handlers.
   * @param {AbortSignal} signal
   */
  #setupDeliveryFrequencyHandlers(signal) {
    if (this.deliveryFrequencyRadios.length === 0) return;

    for (const radio of this.deliveryFrequencyRadios) {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;

        // Update sliding-pill state BEFORE other work so the CSS transition
        // starts in the same frame as the user's click.
        this.#updateDeliveryFrequencyPill(radio);

        // Badge reflects the selected plan regardless of which purchase type is active
        this.#updateSubscriptionSavingsBadge();

        // Only update selling plan if subscription is active (not one-time purchase)
        if (this.#isOneTimePurchase) return;

        const sellingPlanId = radio.dataset.sellingPlanId;
        if (sellingPlanId) {
          this.#updateSellingPlan(sellingPlanId);
        }
        this.#dispatchPriceChange();
      }, { signal });
    }
  }

  /**
   * Seeds the sliding-pill state on first connect: finds the initially-checked
   * radio, records its index, and sets the pill width var so the pill spans
   * that label correctly. Mirrors the size/flavor variant picker behavior.
   */
  #initDeliveryFrequencyPillState() {
    if (!this.deliveryFrequencyGroup || this.deliveryFrequencyRadios.length === 0) return;
    const radios = Array.from(this.deliveryFrequencyRadios);
    // radio.checked is the source of truth; data-current-checked may be stale
    // after a morph patch on variant change.
    const initialIndex = radios.findIndex((r) => r.checked);
    // Reconcile data attrs to the actual checked state so the pill CSS doesn't
    // animate from a phantom "previous" position on first interaction.
    for (const r of radios) {
      r.dataset.currentChecked = r.checked ? 'true' : 'false';
      r.dataset.previousChecked = 'false';
    }
    if (initialIndex === -1) return;
    this.deliveryFrequencyCheckedIndices = [initialIndex];
    const initialLabel = radios[initialIndex].parentElement;
    if (initialLabel) {
      this.deliveryFrequencyGroup.style.setProperty('--pill-width-current', `${initialLabel.offsetWidth}px`);
    }
  }

  /**
   * Updates the sliding-pill state after a delivery-frequency change:
   *  - flips data-current-checked / data-previous-checked on the relevant radios
   *  - records the new [current, previous] index pair (max length 2)
   *  - measures the involved label widths and writes them to CSS custom
   *    properties so the pill can stretch across labels of different widths
   *    during the in-flight transition.
   * @param {HTMLInputElement} newCheckedRadio
   */
  #updateDeliveryFrequencyPill(newCheckedRadio) {
    if (!this.deliveryFrequencyGroup) return;
    const radios = Array.from(this.deliveryFrequencyRadios);
    const newIndex = radios.indexOf(newCheckedRadio);
    if (newIndex === -1) return;

    // Clear data-* on whichever radios currently hold them.
    const [currentIndex, previousIndex] = this.deliveryFrequencyCheckedIndices;
    if (currentIndex !== undefined && radios[currentIndex]) {
      radios[currentIndex].dataset.previousChecked = 'false';
      radios[currentIndex].dataset.currentChecked = 'false';
    }
    if (previousIndex !== undefined && radios[previousIndex]) {
      radios[previousIndex].dataset.previousChecked = 'false';
    }

    // Push the new index onto the front, keep last 2.
    this.deliveryFrequencyCheckedIndices.unshift(newIndex);
    this.deliveryFrequencyCheckedIndices.length = Math.min(this.deliveryFrequencyCheckedIndices.length, 2);

    const [newCurrentIndex, newPreviousIndex] = this.deliveryFrequencyCheckedIndices;
    if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
      radios[newCurrentIndex].dataset.currentChecked = 'true';
    }
    if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
      radios[newPreviousIndex].dataset.previousChecked = 'true';
      radios[newPreviousIndex].dataset.currentChecked = 'false';
    }

    // Measure label widths and apply to the radiogroup (read then write to
    // keep layout flushes batched).
    const currentLabel = newCurrentIndex !== undefined ? radios[newCurrentIndex]?.parentElement : null;
    const previousLabel = newPreviousIndex !== undefined ? radios[newPreviousIndex]?.parentElement : null;
    const currentWidth = currentLabel?.offsetWidth;
    const previousWidth = previousLabel?.offsetWidth;
    if (currentWidth) {
      this.deliveryFrequencyGroup.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else {
      this.deliveryFrequencyGroup.style.removeProperty('--pill-width-current');
    }
    if (previousWidth) {
      this.deliveryFrequencyGroup.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else {
      this.deliveryFrequencyGroup.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Wires up the commitment-notice help tooltips. Hover/focus visibility is
   * CSS-driven; JS adds tap-toggle for touch devices, Escape dismissal even
   * while hovered (WCAG 1.4.13), outside-tap close, and keeps the trigger's
   * aria-expanded in sync with visibility.
   * @param {AbortSignal} signal
   */
  #setupCommitmentTooltips(signal) {
    const tooltips = this.querySelectorAll('[data-commitment-tooltip]');
    if (tooltips.length === 0) return;

    /**
     * @param {Element} tooltip
     * @param {boolean} expanded
     */
    const setExpanded = (tooltip, expanded) => {
      tooltip.querySelector('.commitment-tooltip__trigger')?.setAttribute('aria-expanded', String(expanded));
    };

    for (const tooltip of tooltips) {
      const trigger = tooltip.querySelector('.commitment-tooltip__trigger');
      if (!trigger) continue;

      trigger.addEventListener('click', () => {
        tooltip.classList.remove('commitment-tooltip--dismissed');
        const isOpen = tooltip.classList.toggle('commitment-tooltip--open');
        setExpanded(tooltip, isOpen);
      }, { signal });

      tooltip.addEventListener('pointerenter', () => {
        if (!tooltip.classList.contains('commitment-tooltip--dismissed')) {
          setExpanded(tooltip, true);
        }
      }, { signal });

      tooltip.addEventListener('pointerleave', () => {
        // Clear the Escape-dismissed state so the next hover works again
        tooltip.classList.remove('commitment-tooltip--dismissed');
        if (!tooltip.classList.contains('commitment-tooltip--open') && !tooltip.contains(document.activeElement)) {
          setExpanded(tooltip, false);
        }
      }, { signal });

      tooltip.addEventListener('focusin', () => {
        if (!tooltip.classList.contains('commitment-tooltip--dismissed')) {
          setExpanded(tooltip, true);
        }
      }, { signal });

      tooltip.addEventListener('focusout', (event) => {
        if (tooltip.contains(/** @type {Node|null} */ (event.relatedTarget))) return;
        tooltip.classList.remove('commitment-tooltip--open', 'commitment-tooltip--dismissed');
        setExpanded(tooltip, false);
      }, { signal });
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      for (const tooltip of tooltips) {
        const isVisible = tooltip.classList.contains('commitment-tooltip--open')
          || tooltip.matches(':hover')
          || tooltip.contains(document.activeElement);
        if (!isVisible) continue;
        tooltip.classList.add('commitment-tooltip--dismissed');
        tooltip.classList.remove('commitment-tooltip--open');
        setExpanded(tooltip, false);
      }
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      for (const tooltip of tooltips) {
        if (!tooltip.classList.contains('commitment-tooltip--open')) continue;
        if (tooltip.contains(/** @type {Node|null} */ (event.target))) continue;
        tooltip.classList.remove('commitment-tooltip--open');
        setExpanded(tooltip, false);
      }
    }, { signal });
  }

  /**
   * Resets to one-time purchase when the variant changes, since selling plan
   * allocations may differ between variants.
   * @param {AbortSignal} signal
   */
  #setupVariantChangeHandler(signal) {
    document.addEventListener('variant:update', (event) => {
      const productId = this.dataset.productId;
      if (!productId || event.detail?.data?.productId !== productId) return;

      // Update cached variant price from event resource (the variant object)
      const newVariant = event.detail.resource;
      if (newVariant?.price != null) {
        this.dataset.variantPrice = String(newVariant.price);
      }

      // Recalculate bulk discount prices from new variant price
      this.#recalcBulkDiscountPrices();

      // Clear selling plan input as a safety measure before validation.
      // If the re-rendered HTML is missing or validation fails, we won't
      // accidentally submit a stale selling plan for the wrong variant.
      this.#updateSellingPlan(null);

      // Update delivery frequency prices from re-rendered HTML
      this.#updateDeliveryFrequencyPrices(event.detail.data?.html);

      // Ensure the currently selected plan still exists for this variant
      this.#validateSelectedPlan(event.detail.data?.html);

      // Sync form state based on current radio selection.
      // Delivery frequency data attributes were already updated above for
      // the new variant, so #syncState reads the correct plan ID and price.
      // This restores the selling plan input if a valid plan is selected.
      this.#syncState();

      // The morph may have swapped the set of delivery-frequency radios
      // (different selling plans per variant). Re-snapshot and reset the
      // pill state so the slide effect tracks the new selection cleanly
      // without a stale [current, previous] pair from the old variant.
      this.deliveryFrequencyRadios = this.querySelectorAll('input[name^="delivery-frequency-"]');
      this.deliveryFrequencyGroup = this.querySelector('.delivery-frequency-options');
      this.deliveryFrequencyCheckedIndices = [];
      this.#initDeliveryFrequencyPillState();
    }, { signal });
  }

  /**
   * Recalculates bulk discount option prices and displayed text based on the
   * current variant price. Supports both percentage and fixed-amount discounts
   * (DiscountKit FIXED_AMOUNT tiers store the raw amount in data-discount-amount).
   */
  #recalcBulkDiscountPrices() {
    const variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;

    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    for (const option of this.bulkDiscountOptions) {
      const discountType = option.dataset.discountType || 'PERCENTAGE';
      let discountedPrice;

      if (discountType === 'PERCENTAGE') {
        const discountPercent = parseInt(option.dataset.discountPercent, 10) || 0;
        discountedPrice = Math.round(variantPrice - (variantPrice * discountPercent / 100));
      } else {
        // Fixed amount discount — use the raw discount amount in minor currency units
        const discountAmount = parseInt(option.dataset.discountAmount, 10) || 0;
        discountedPrice = Math.max(0, variantPrice - discountAmount);
        // Update the displayed savings percentage for the new variant price
        if (variantPrice > 0) {
          option.dataset.discountPercent = String(Math.round(discountAmount * 100 / variantPrice));
        }
      }

      const oldPrice = parseInt(option.dataset.discountedPrice, 10) || 0;
      option.dataset.discountedPrice = String(discountedPrice);

      // Update displayed price text
      const priceEl = option.closest('label')?.querySelector('.bulk-discount-option__price');
      if (priceEl) {
        const unitEl = priceEl.querySelector('.bulk-discount-option__unit');
        const unitText = unitEl?.textContent || '';
        priceEl.textContent = this.#formatPrice(discountedPrice);
        if (unitText) {
          const span = document.createElement('span');
          span.className = 'bulk-discount-option__unit';
          span.textContent = unitText;
          priceEl.appendChild(span);
        }

        // Directional fade-through: old value exits up, new value enters from below
        if (oldPrice !== discountedPrice && !reducedMotion) {
          priceEl.getAnimations().forEach((a) => a.cancel());
          priceEl.animate(
            [
              { opacity: 1, transform: 'translateY(0)' },
              { opacity: 0, transform: 'translateY(-4px)', offset: 0.3 },
              { opacity: 0, transform: 'translateY(4px)', offset: 0.31 },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
          );
        }
      }
    }
  }

  /**
   * Updates delivery frequency radio/hidden input prices from the re-rendered
   * section HTML, since selling plan allocations are variant-specific.
   * @param {Document|undefined} html - The re-rendered section HTML document
   */
  #updateDeliveryFrequencyPrices(html) {
    if (!html) return;

    const newPicker = html.querySelector(`selling-plan-picker[data-product-id="${this.dataset.productId}"]`);
    if (!newPicker) return;

    // Scope to the delivery frequency wrapper so we don't accidentally match
    // the subscription radio (which shares the same data-selling-plan-id but
    // lacks pricing data attributes).
    const newFreqWrapper = newPicker.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency');
    if (!newFreqWrapper) return;

    // Update delivery frequency radio inputs
    for (const radio of this.deliveryFrequencyRadios) {
      const planId = radio.dataset.sellingPlanId;
      if (!planId) continue;
      const newRadio = /** @type {HTMLInputElement|null} */ (newFreqWrapper.querySelector(`input[data-selling-plan-id="${planId}"]`));
      if (newRadio) {
        radio.dataset.adjustedPrice = newRadio.dataset.adjustedPrice || '';
        radio.dataset.compareAtPrice = newRadio.dataset.compareAtPrice || '';
        radio.dataset.basePrice = newRadio.dataset.basePrice || '';
        radio.dataset.savingsPercent = newRadio.dataset.savingsPercent || '0';
      }
    }

    // Update single-plan hidden input if present
    const hiddenInput = /** @type {HTMLInputElement|null} */ (this.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency > input[type="hidden"][data-adjusted-price]'));
    if (hiddenInput) {
      const planId = hiddenInput.dataset.sellingPlanId;
      const newHidden = /** @type {HTMLInputElement|null} */ (newFreqWrapper.querySelector(`input[data-selling-plan-id="${planId}"]`));
      if (newHidden) {
        hiddenInput.dataset.adjustedPrice = newHidden.dataset.adjustedPrice || '';
        hiddenInput.dataset.compareAtPrice = newHidden.dataset.compareAtPrice || '';
        hiddenInput.dataset.basePrice = newHidden.dataset.basePrice || '';
        hiddenInput.dataset.savingsPercent = newHidden.dataset.savingsPercent || '0';
      }
    }
  }

  /**
   * Validates that the currently selected delivery frequency plan still exists
   * for the new variant. Unchecks stale radios and falls back to the first
   * available plan so #syncState never submits an invalid selling_plan ID.
   * @param {Document|undefined} html - The re-rendered section HTML document
   */
  #validateSelectedPlan(html) {
    if (!html) return;

    const newPicker = html.querySelector(`selling-plan-picker[data-product-id="${this.dataset.productId}"]`);
    if (!newPicker) return;

    const newFreqWrapper = newPicker.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency');

    // Check each delivery frequency radio against the new variant's allocations
    for (const radio of this.deliveryFrequencyRadios) {
      const planId = radio.dataset.sellingPlanId;
      if (!planId) continue;
      const existsInNewVariant = newFreqWrapper?.querySelector(`input[data-selling-plan-id="${planId}"]`);
      if (!existsInNewVariant) {
        radio.checked = false;
      }
    }

    // If no delivery frequency radio is checked, select the first available one
    const anyChecked = this.querySelector('input[name^="delivery-frequency-"]:checked');
    if (!anyChecked) {
      const firstAvailable = /** @type {HTMLInputElement|null} */ (this.querySelector('input[name^="delivery-frequency-"]'));
      if (firstAvailable) {
        firstAvailable.checked = true;
      }
    }

    // Validate single-plan hidden input
    const hiddenInput = /** @type {HTMLInputElement|null} */ (this.querySelector('.selling-plan-picker__content-wrapper--delivery-frequency > input[type="hidden"][data-adjusted-price]'));
    if (hiddenInput) {
      const planId = hiddenInput.dataset.sellingPlanId;
      const existsInNew = newFreqWrapper?.querySelector(`input[data-selling-plan-id="${planId}"]`);
      if (!existsInNew) {
        hiddenInput.value = '';
        hiddenInput.dataset.sellingPlanId = '';
      }
    }
  }

  /**
   * Formats a price in minor currency units using the shop's money format.
   * @param {number} priceInMinorUnits
   * @returns {string}
   */
  #formatPrice(priceInMinorUnits) {
    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    if (!format || !currency) {
      return (priceInMinorUnits / 100).toFixed(2);
    }
    return formatMoney(priceInMinorUnits, format, currency);
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
